// Keeps users on the latest deployed app version.
//
// The build writes /version.json (scripts/updateVersionFromBuild.js) and injects the
// same version into index.html as window.__APP_VERSION__. The running app compares
// the two on load, when the tab regains focus/visibility, and periodically.

export const AUTO_RELOAD_KEY = '_auto_reload_attempted_for_version';
const CHUNK_RELOAD_KEY = '_chunk_error_reload_at';
const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;

// A version we can meaningfully compare. Local dev builds report "0.0.0" (no
// build number), which must never trigger a reload.
export function isUsableVersion(v) {
  return typeof v === 'string' && v.trim().length > 0 && v.trim() !== '0.0.0';
}

export function isOutdated(localVersion, remoteVersion) {
  return isUsableVersion(localVersion)
    && isUsableVersion(remoteVersion)
    && localVersion.trim() !== remoteVersion.trim();
}

// Decide what to do when the deployed version differs from the running one.
//   trigger: 'load' | 'focus' | 'interval'
// Returns 'none' | 'reload' | 'defer'.
// Slimmed vs Front: no Tupas/auth special-cases; when the user is actively
// filling the form (interval + visible tab) we defer until the next focus.
export function decideUpdateAction({ localVersion, remoteVersion, trigger, hidden, alreadyTriedFor }) {
  if (!isOutdated(localVersion, remoteVersion)) return 'none';

  // Already reloaded once for this version in this tab and still outdated
  // (e.g. a cache in between): don't loop.
  if (alreadyTriedFor && alreadyTriedFor === remoteVersion) return 'none';

  // Safe moments to reload without losing mid-form typing:
  // app start, the user just came back to the tab, or the tab is in the background.
  if (trigger === 'load' || trigger === 'focus' || hidden) return 'reload';

  // User is actively working: wait for the next focus/visibility change.
  return 'defer';
}

function isChunkLoadError(reason) {
  if (!reason) return false;
  const name = reason.name || '';
  const msg = String(reason.message || reason);
  return name === 'ChunkLoadError'
    || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg);
}

// After a deploy, a tab running the old bundle may request lazy chunks that no
// longer exist. Reload once (at most every 60 s) so the user gets the new build
// instead of a broken page.
export function installChunkErrorReload(win = window) {
  const handler = (reason) => {
    if (!isChunkLoadError(reason)) return;
    try {
      const last = Number(win.sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
      if (Date.now() - last < 60000) return;
      win.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch (e) { /* ignore */ }
    win.location.reload();
  };
  win.addEventListener('unhandledrejection', (e) => handler(e && e.reason));
  win.addEventListener('error', (e) => handler(e && (e.error || e)));
}

// The app no longer uses a service worker (registerServiceWorker() is disabled).
// Remove any registration left over from older versions so it cannot keep
// serving a cached old app shell.
export function unregisterServiceWorkers(nav = (typeof navigator !== 'undefined' ? navigator : undefined), win = window) {
  try {
    if (nav && nav.serviceWorker && nav.serviceWorker.getRegistrations) {
      nav.serviceWorker.getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .catch(() => {});
    }
    if (win && win.caches && win.caches.keys) {
      win.caches.keys()
        .then((names) => Promise.all(names
          .filter((n) => /workbox|precache|sw-precache/i.test(n))
          .map((n) => win.caches.delete(n))))
        .catch(() => {});
    }
  } catch (e) { /* best effort */ }
}

function readLocalVersion(win = window) {
  try {
    if (win && win.__APP_VERSION__) return String(win.__APP_VERSION__);
  } catch (e) { /* ignore */ }
  return '0.0.0';
}

// Poll /version.json on load, focus/visibility, and every intervalMs. Reloads
// once when the deployed version differs from the running one.
export function startVersionCheck({ intervalMs = DEFAULT_INTERVAL_MS, win = window, doc = document } = {}) {
  let lastCheck = 0;
  let timer = null;
  let stopped = false;
  const localVersion = readLocalVersion(win);

  const check = async (trigger) => {
    if (stopped) return;
    if (trigger === 'focus' && Date.now() - lastCheck < 30000) return;
    lastCheck = Date.now();
    try {
      const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL
        ? process.env.PUBLIC_URL
        : '').replace(/\/$/, '');
      const res = await win.fetch(`${base}/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          pragma: 'no-cache',
          'cache-control': 'no-cache',
        },
      });
      if (!res.ok) return;
      const data = await res.json();
      const remote = (data && (data.version || data.buildNumber)) || '';
      let alreadyTriedFor = null;
      try { alreadyTriedFor = win.sessionStorage.getItem(AUTO_RELOAD_KEY); } catch (e) { /* ignore */ }

      const action = decideUpdateAction({
        localVersion,
        remoteVersion: remote,
        trigger,
        hidden: doc.visibilityState === 'hidden',
        alreadyTriedFor,
      });

      if (action === 'reload') {
        try { win.sessionStorage.setItem(AUTO_RELOAD_KEY, remote); } catch (e) { /* ignore */ }
        win.location.reload();
      }
    } catch (e) {
      // ignore network errors
    }
  };

  const onFocus = () => { check('focus'); };
  const onVisibility = () => { if (doc.visibilityState === 'visible') check('focus'); };

  check('load');
  timer = win.setInterval(() => check('interval'), intervalMs);
  win.addEventListener('focus', onFocus);
  doc.addEventListener('visibilitychange', onVisibility);

  return () => {
    stopped = true;
    if (timer) win.clearInterval(timer);
    win.removeEventListener('focus', onFocus);
    doc.removeEventListener('visibilitychange', onVisibility);
  };
}
